import { Camera, FreeCamera, Scene, Vector3 } from '@babylonjs/core';

const HEIGHT = 80;
const HALF_VIEW = 40; // vertical half-extent in meters; horizontal scales with aspect

export interface TopDownOptions {
  name?: string;
  /** True = rotate the world so the car's forward is "up" on screen.
   *  False = fixed north-up (world +Z is up). */
  headingUp?: boolean;
  /** Distance ahead of the car (in meters) to center the view. Pushes the car toward the
   *  bottom of the screen so more is visible in the travel direction. */
  forwardOffset?: number;
}

export class TopDownCamera {
  readonly camera: FreeCamera;
  private headingUp: boolean;
  private forwardOffset: number;
  private aspect = 1.0;
  /** Multiplier on the ortho extents. 1 = normal framing; larger = zoomed out (more area
   *  visible). Driven by the teleport cinematic. */
  private zoom = 1.0;

  constructor(scene: Scene, opts: TopDownOptions = {}) {
    this.headingUp = opts.headingUp ?? false;
    this.forwardOffset = opts.forwardOffset ?? 0;
    this.camera = new FreeCamera(opts.name ?? 'topdown', new Vector3(0, HEIGHT, 0), scene);
    this.camera.mode = Camera.ORTHOGRAPHIC_CAMERA;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 5000; // raised so the topdown stays valid even when zoomed far out
    // A camera looking straight down (-Y) has the default upVector (0,1,0) parallel to view
    // direction — degenerate. For north-up we anchor "up" to world +Z; for heading-up we
    // overwrite it per frame with the car's forward vector.
    if (!this.headingUp) {
      this.camera.upVector.set(0, 0, 1);
    }
  }

  resize(width: number, height: number): void {
    this.aspect = height > 0 ? width / height : 1;
    this.applyExtents();
  }

  /** Scale the ortho extents by `factor`. 1 = normal, >1 = zoomed out. */
  setZoom(factor: number): void {
    this.zoom = factor;
    this.applyExtents();
  }

  getZoom(): number {
    return this.zoom;
  }

  private applyExtents(): void {
    const half = HALF_VIEW * this.zoom;
    this.camera.orthoTop = half;
    this.camera.orthoBottom = -half;
    this.camera.orthoLeft = -half * this.aspect;
    this.camera.orthoRight = half * this.aspect;
  }

  update(targetPos: Vector3, forward: Vector3): void {
    const fx = forward.x;
    const fz = forward.z;
    const cx = targetPos.x + fx * this.forwardOffset;
    const cz = targetPos.z + fz * this.forwardOffset;
    this.camera.position.set(cx, targetPos.y + HEIGHT, cz);
    if (this.headingUp) {
      this.camera.upVector.set(fx, 0, fz);
    }
    // Look straight down at the same XZ as the camera (so the view stays orthogonal to ground).
    this.camera.setTarget(new Vector3(cx, targetPos.y, cz));
  }

  snap(targetPos: Vector3, forward: Vector3): void {
    this.update(targetPos, forward);
  }
}
